import _ from 'lodash'
import moment from 'moment-timezone'
import { useMemo } from 'react'
import { createSelector } from 'reselect'
import { gql } from '@apollo/client'
import { message } from 'antd'
import adminQuotingPageFragments from '@Admin/AdminQuotingPage/fragments'
import { DFM_TAGS_MAP } from '@Admin/AdminQuotingPage/constants'

import {
    QUOTE_SUMMARY_STATUSES,
    DRAFT,
    PURCHASED,
    REORDERING,
    ISPUBLISHED,
} from '~/lib/quoteStatuses'
import { DEFAULT_VALUE_DASH } from '~/lib/getPriceDisplay'
import logger from '~/lib/logger'
import {
    QUOTE_MODEL_REVISION_STATES,
    QUOTE_MODEL_REVISION_FLAGS,
} from '~/lib/quoteConstants'
import { PROCESSID_IM } from '~/lib/partsConstants'
import { QuoteDetailsDocument } from '~/gql/graphql'

import {
    ODP_RELEASE_DFM_DESCRIPTION,
    RELEASE_DFM_DESCRIPTION,
    RELEASE_QUOTE_DESCRIPTION,
} from './constants'
import fragments from './fragments'

export const IM_REMOVE_SALES_REP_MODAL_TITLE =
    'Are you sure you want to remove the sales rep contact info?'

export const PLACEMENT = 'admin-quote-summary'
export const POLLING_INTERVAL = 5000
export const QMR_STATE_PROCESSING = 'processing'
const QUOTE_DELETED = 'Deleted'
const PART_DELETED = 'deleted'

export const createCSVFileName = quoteName => {
    const dateTime = moment().format('YYYYMMDD_hh.mm.ssA')
    const extension = 'csv'

    return `${quoteName}_${dateTime}.${extension}`
}
const getQuoteForSelector = quote => quote

export const getModelTableItems = createSelector(getQuoteForSelector, quote => {
    const quoteSummaryStatus = quote.summaryStatus
    const quoteModels = quote.quoteModels.items

    return quoteModels
        .filter(quoteModel => {
            return (
                (quoteModel.selectedQuoteModelRevision.state !== PART_DELETED &&
                    quoteModel.deletedAt === null) ||
                quoteSummaryStatus === QUOTE_DELETED
            )
        })
        .map(quoteModel => {
            return {
                item: quoteModel,
                key: quoteModel.id,
            }
        })
})

export const getMoldTableItems = quoteModelItems =>
    quoteModelItems
        ?.filter(quoteModelItem => quoteModelItem.deletedAt === null)
        .map(quoteModelItem => {
            const { fileName, mold } = quoteModelItem.selectedQuoteModelRevision

            return mold && { fileName, mold }
        })
        ?.filter(Boolean)

export const getAttachedFiles = quoteModelsItems => {
    return quoteModelsItems
        .filter(item => {
            if (item.selectedQuoteModelRevision.attachmentLink !== null) {
                return item
            }
        })
        .map(item => {
            return item.selectedQuoteModelRevision.attachmentLink
                .quoteAttachmentRevision.id
        })
}

export const getAttachmentTableItems = (
    quoteAttachmentItems = {} || [],
    quoteModelItems = {} || [],
) => {
    if (
        !Array.isArray(quoteAttachmentItems) ||
        !Array.isArray(quoteModelItems)
    ) {
        return []
    }

    const attachedFilesIds = getAttachedFiles(quoteModelItems)

    return quoteAttachmentItems
        .filter(({ latestAttachmentRevision }) => {
            return !attachedFilesIds.includes(latestAttachmentRevision.id)
        })
        .map(({ latestAttachmentRevision }) => {
            return {
                item: latestAttachmentRevision,
                key: latestAttachmentRevision.id,
            }
        })
}

const {
    REGISTERED,
    PROCESSING,
    NEEDS_CONFIGURATION,
    NEEDS_RFQ,
    UNRESOLVED_DFM,
    NOT_MANUFACTURABLE,
    PURCHASABLE,
    ERROR,
    DELETED,
} = QUOTE_MODEL_REVISION_STATES

const {
    EMPTY_QUOTE,
    FILES_MISSING_CONFIGURATION,
    NEEDS_RFQ_HAS_FAILURES,
    NEEDS_RFQ: QUOTE_NEEDS_RFQ,
    AWAITING_QUOTATION,
    HAS_FAILURES,
    PURCHASABLE_WITH_WARNINGS,
    PURCHASABLE: QUOTE_PURCHASABLE,
    PURCHASED: QUOTE_PURCHASED,
    UNMATCHED,
} = QUOTE_SUMMARY_STATUSES

const SUMMARY_STATUS_MESSAGE_MAP = {
    [EMPTY_QUOTE]: 'Empty',
    [FILES_MISSING_CONFIGURATION]: 'Missing Configuration',
    [NEEDS_RFQ_HAS_FAILURES]: 'Parts require RFQ w/ failures',
    [QUOTE_NEEDS_RFQ]: 'Parts require RFQ',
    [AWAITING_QUOTATION]: 'Requested',
    [HAS_FAILURES]: 'Requested w/ failures',
    [PURCHASABLE_WITH_WARNINGS]: 'Ready to purchase w/ warnings',
    [QUOTE_PURCHASABLE]: 'Ready to purchase',
    [QUOTE_PURCHASED]: 'Purchased',
    [UNMATCHED]: '',
}

const SUMMARY_STATUS_COLOR_MAP = {
    [EMPTY_QUOTE]: '',
    [FILES_MISSING_CONFIGURATION]: 'purple',
    [NEEDS_RFQ_HAS_FAILURES]: 'red',
    [QUOTE_NEEDS_RFQ]: 'magenta',
    [AWAITING_QUOTATION]: 'orange',
    [HAS_FAILURES]: 'red',
    [PURCHASABLE_WITH_WARNINGS]: 'gold',
    [QUOTE_PURCHASABLE]: 'green',
    [PURCHASED]: 'green',
    [DELETED]: 'red',
    [UNMATCHED]: '',
}

const STATE_COLOR_MAP = {
    [REGISTERED]: '',
    [PROCESSING]: '',
    [NEEDS_CONFIGURATION]: 'purple',
    [NEEDS_RFQ]: 'magenta',
    [UNRESOLVED_DFM]: 'gold',
    [NOT_MANUFACTURABLE]: 'volcano',
    [PURCHASABLE]: 'green',
    [ERROR]: 'red',
    [DELETED]: 'red',
}

const QMR_STATE_MESSAGE_MAP = {
    [REGISTERED]: 'Registered',
    [PROCESSING]: 'Processing...',
    [NEEDS_CONFIGURATION]: 'Needs Configuration',
    [NEEDS_RFQ]: 'RFQ required',
    [UNRESOLVED_DFM]: 'Warnings',
    [NOT_MANUFACTURABLE]: 'Failed DFM',
    [PURCHASABLE]: 'Purchasable',
    [ERROR]: 'Error',
    [DELETED]: 'Deleted',
}

const { NOTREORDERABLE } = QUOTE_MODEL_REVISION_FLAGS

const QMR_FLAGS_MESSAGE_MAP = {
    [NOTREORDERABLE]: 'Not reorderable',
}

const QMR_FLAGS_COLOR_MAP = {
    [NOTREORDERABLE]: 'red',
}

export const getSummaryStatusMessage = summaryStatus =>
    SUMMARY_STATUS_MESSAGE_MAP[summaryStatus] || summaryStatus

export const getSummaryStatusColor = summaryStatus => {
    return SUMMARY_STATUS_COLOR_MAP[summaryStatus.toLowerCase()] || ''
}

export const getStateColor = (qmrState, quoteState) => {
    if (quoteState === PURCHASED) {
        return 'blue'
    }
    return STATE_COLOR_MAP[qmrState] || ''
}

export const getStateMessage = (qmrState, quoteState) => {
    if (quoteState === PURCHASED) {
        return 'Purchased'
    }
    return QMR_STATE_MESSAGE_MAP[qmrState] || qmrState
}

export const useReorder = quote =>
    useMemo(() => quote?.status === REORDERING, [quote])

export const useAllPartsReportsReleased = quote => {
    return useMemo(() => {
        const publishedFlag = quote?.quoteModels?.items.some(quoteModel => {
            const reports = quoteModel.selectedQuoteModelRevision.dfmReports

            const dfmReport =
                quoteModel.selectedQuoteModelRevision.dfmReports.filter(
                    report => report[ISPUBLISHED] === false,
                )

            const dfmFeedback =
                quoteModel.selectedQuoteModelRevision.dfmFeedback.filter(
                    feedback => feedback[ISPUBLISHED] === false,
                )

            return reports.length ? dfmReport.length : dfmFeedback.length
        })

        return publishedFlag
    }, [quote.quoteModels.items])
}

export const getFlagMessage = qmrFlag =>
    QMR_FLAGS_MESSAGE_MAP[qmrFlag] || qmrFlag
export const getFlagColor = qmrFlag => QMR_FLAGS_COLOR_MAP[qmrFlag] || 'red'

export function noConfigFallback(text) {
    return text || DEFAULT_VALUE_DASH
}

export function materialRender(modelConfiguration) {
    const material = modelConfiguration?.material?.name || ''
    const color = modelConfiguration?.color?.name || ''
    const colorCode = modelConfiguration?.colorCode
    const shore = modelConfiguration?.shore?.name || ''
    const texture = modelConfiguration?.texture?.name || ''
    const infill = modelConfiguration?.fdmInfillPercentageCategoryLabel
        ? `${modelConfiguration?.fdmInfillPercentageCategoryLabel} Infill`
        : null
    let text = material
    const colorText = colorCode ? `${color} ${colorCode}` : color

    if (colorText || shore || texture) {
        text += ' ('
        text += [colorText, shore, texture, infill].filter(el => el).join(', ')
        text += ')'
    }

    return text || DEFAULT_VALUE_DASH
}

export function postProcessRender(pricingSummary) {
    const postProcesses = pricingSummary?.postProcesses || []

    const pricingSummaryDetails = postProcesses.map(postProcess => {
        const [name, color, thickness] = _.at(
            postProcess,
            'postProcess.name',
            'postProcessColor.name',
            'postProcessThickness.name',
        )

        const colorAndThickness = color
            ? `(${_.join(_.compact([color, thickness]), ', ')})`
            : ''

        return `${name} ${colorAndThickness}`
    })

    return pricingSummaryDetails
}

export function secondaryProcessRender(secondaryProcesses = []) {
    const secondaryProcessList = secondaryProcesses.map(process => {
        return process?.process?.name || ''
    })

    return secondaryProcessList
}

export function releaseImDfmReport({
    client,
    quoteId,
    orderId,
    skipDfmEmail,
    emailInput,
}) {
    const mutation = gql`
        mutation releaseDfmReports(
            $quoteId: UUID!
            $orderId: String
            $skipDfmEmail: Boolean
            $emailInput: QuoteReadyEmailInput
        ) {
            releaseDfmReports(
                quoteId: $quoteId
                orderId: $orderId
                skipDfmEmail: $skipDfmEmail
                emailInput: $emailInput
            ) {
                id
            }
        }
    `

    return client.mutate({
        mutation,
        variables: {
            quoteId,
            orderId,
            skipDfmEmail,
            emailInput,
        },
        refetchQueries: [
            {
                query: QuoteDetailsDocument,
                variables: { id: quoteId },
            },
        ],
    })
}

export function releaseAllDfm({ client, quoteId, skipEmails, emailInput }) {
    const mutation = gql`
        mutation publishDfmAndInspectionDataForQuote(
            $quoteId: UUID!
            $skipEmails: Boolean
            $emailInput: QuoteReadyEmailInput
        ) {
            publishDfmAndInspectionDataForQuote(
                quoteId: $quoteId
                skipEmails: $skipEmails
                emailInput: $emailInput
            ) {
                id
                name
                status
                user {
                    id
                    email
                    displayName
                }
                quoteModels {
                    totalCount
                    items {
                        id
                        selectedQuoteModelRevision {
                            id
                            version
                            state
                            partRevision {
                                id
                                thumbnailUri
                                downloadUri
                                status
                                modelFileName
                                boundingBox {
                                    x
                                    y
                                    z
                                }
                                analysisErrorCode
                            }
                            attachmentLink {
                                id
                                quoteAttachmentRevision {
                                    id
                                    attachment {
                                        ...adminAttachment
                                    }
                                }
                            }
                            fileName
                            createdAt
                            updatedAt
                            annotations {
                                id
                            }
                            modelConfiguration {
                                ...adminQuoteDetailsModelConfiguration
                            }
                        }
                    }
                }
                quoteAttachments {
                    totalCount
                    items {
                        id
                        latestAttachmentRevision {
                            id
                            attachment {
                                ... on Attachment {
                                    id
                                    fileName
                                    downloadUrl
                                }
                            }
                        }
                    }
                }
            }
        }
        ${adminQuotingPageFragments.Attachment}
        ${fragments.adminQuoteDetailsModelConfiguration}
    `

    return client.mutate({
        mutation,
        variables: {
            quoteId,
            skipEmails,
            emailInput,
        },
        refetchQueries: [
            {
                query: QuoteDetailsDocument,
                variables: { id: quoteId },
            },
        ],
    })
}

export const updateNotesToCustomerMutation = gql`
    mutation updateNotesToCustomerMutation($quoteId: UUID!, $notes: String) {
        updateNotesToCustomer(quoteId: $quoteId, notes: $notes) {
            id
            notesToCustomer
        }
    }
`

export const updateNotesToCustomerByQuoteId = async ({
    quoteId,
    updatedNotes,
    refetchQuery,
    updateNotesMutationFunction,
    setIsLoading,
    setIsModalVisible,
}) => {
    try {
        await updateNotesMutationFunction({
            variables: {
                quoteId,
                notes: updatedNotes,
            },

            refetchQueries: [...refetchQuery],
        })
        setIsModalVisible(false)
    } catch (error) {
        logger.exception(error, {
            extra: {
                NotesToCustomerModal:
                    'Failed to update additional requirement notes',
                quoteId,
            },
        })
        message.error('Failed to update notes to customer')
    } finally {
        setIsLoading(true)
    }
}

export const updateSalesRepContactInfoMutation = gql`
    mutation updateSalesRepContactInfoMutation(
        $quoteId: UUID!
        $email: String
    ) {
        upsertQuoteSalesRep(quoteId: $quoteId, salesRepEmail: $email) {
            id
            salesRep {
                id
                email
                phoneNumber
                displayName
            }
        }
    }
`

export const updateSalesRepInfoByEmail = async ({
    quoteId,
    updatedSalesRepEmail,
    refetchQuery,
    updateSalesRepContactInfoMutationFunction,
    setIsLoading,
    setIsModalVisible,
}) => {
    try {
        await updateSalesRepContactInfoMutationFunction({
            variables: {
                quoteId,
                email: updatedSalesRepEmail,
            },

            refetchQueries: [...refetchQuery],
        })
        setIsModalVisible(false)
    } catch (error) {
        logger.exception(error, {
            extra: {
                SalesRepContactInfoModal:
                    'Failed to update sales rep contact info',
                quoteId,
            },
        })
        message.error('Please enter valid email')
    } finally {
        setIsLoading(true)
    }
}

export function formatPhoneNumber(phoneNumber, withCountryCode = false) {
    if (typeof phoneNumber !== 'string') {
        return ''
    }
    const trimmedPhoneNumber = phoneNumber.replace(/\D/g, '')
    // only handle US number for now
    const matchedResults = trimmedPhoneNumber.match(/^(\d{3})(\d{3})(\d{4})$/)

    if (matchedResults) {
        return `${withCountryCode ? '+1 ' : ''}(${matchedResults[1]}) ${
            matchedResults[2]
        }-${matchedResults[3]}`
    }
    return phoneNumber
}

export function getLastActivityInfo(activities, userEmail, adminEmail) {
    let lastActivityTime, lastActivityActorEmail

    if (!Array.isArray(activities)) {
        return {}
    }

    if (userEmail === adminEmail) {
        return {
            lastActivityTime: activities[0]?.updatedAt,
            lastActivityActorEmail: activities[0]?.actor?.email,
        }
    }

    for (const activity of activities) {
        if (activity?.actor?.email && activity.actor.email !== adminEmail) {
            lastActivityTime = activity.updatedAt
            lastActivityActorEmail = activity.actor.email
            break
        }
    }
    return { lastActivityTime, lastActivityActorEmail }
}

export function useGetLastActivityInfo(quote, identity) {
    const { lastActivityTime, lastActivityActorEmail } = useMemo(
        () =>
            getLastActivityInfo(
                quote.activity?.items || [],
                quote.user?.email,
                identity.email,
            ),
        [quote, identity],
    )

    return { lastActivityTime, lastActivityActorEmail }
}

export const useGetQmrFlags = quote =>
    useMemo(() => {
        const flagsSet = new Set()
        const quoteModelItems = quote?.quoteModels?.items || []

        quoteModelItems.forEach(quoteModel => {
            if (quoteModel?.selectedQuoteModelRevision?.flags) {
                quoteModel?.selectedQuoteModelRevision?.flags.forEach(flag =>
                    flagsSet.add(flag),
                )
            }
        })
        return [...flagsSet]
    }, [quote])

export const createMoldToolByQmrId = gql`
    mutation createMoldToolByQmrId(
        $qmrId: UUID!
        $isImMaafcoEnabled: Boolean!
    ) {
        createMoldTool(qmrId: $qmrId) {
            id
            moldToolRevision @include(if: $isImMaafcoEnabled) {
                id
                processId
            }
        }
    }
`

export const createMoldTool = async ({
    qmrId,
    setLoading,
    refetchQuery,
    createMoldToolByQmrIdMutation,
    isImMaafcoEnabled,
}) => {
    if (!refetchQuery) {
        return null
    }
    setLoading(true)

    try {
        return await createMoldToolByQmrIdMutation({
            variables: {
                qmrId,
                isImMaafcoEnabled,
            },
            refetchQueries: () => [...refetchQuery],
            awaitRefetchQueries: true,
        })
    } catch (error) {
        logger.exception(error, {
            extra: {
                info: 'AdminQuoteDetailsPage:: failed to create mold tool',
                qmrId,
            },
        })
        message.error('Mold tool not created')
    } finally {
        setLoading(false)
    }
}

export function showAdminAlertToEditQuote(quote) {
    return quote?.status === DRAFT
}

export const formatEmailInputs = (value = {}) => {
    return {
        emailFrom: value.from,
        emailTo: value.to,
        emailsCC: value.cc || [],
        emailSubject: value.subject,
        emailNotes: value.notes,
    }
}

export const onReleaseDfmClick = releaseQuoteDfm => {
    const {
        quoteId,
        onClose,
        client,
        form,
        skipEmails,
        showCustamizableEmailSection,
    } = releaseQuoteDfm

    form.validateFields(async (error, values) => {
        try {
            const releaseImReportOptions = {
                quoteId,
                client,
                orderId: null,
                skipDfmEmail: skipEmails,
                emailInput: null,
            }

            if (!skipEmails && showCustamizableEmailSection) {
                releaseImReportOptions.emailInput = formatEmailInputs(values)
            }
            await releaseImDfmReport(releaseImReportOptions)
            onClose()
            message.success('Released DFM successfully')
        } catch (err) {
            const errorMsg = 'Failed to release DFM'

            message.error(`${errorMsg}, please try it again`)
            logger.exception(err, {
                extra: {
                    info: `AdminQuoteDetailsPage:: onReleaseDfmClick ${errorMsg}`,
                    quoteId,
                },
            })
        }
    })
}

export const onReleaseQuoteClick = releaseDfmReports => {
    const {
        quoteId,
        onClose,
        setIsReleasing,
        client,
        form,
        skipEmails,
        showCustamizableEmailSection,
    } = releaseDfmReports

    form.validateFields(async (error, values) => {
        if (setIsReleasing) {
            setIsReleasing(true)
        }
        try {
            const releaseQuoteOptions = {
                quoteId,
                client,
                skipEmails,
                emailInput: null,
            }

            if (!skipEmails && showCustamizableEmailSection) {
                releaseQuoteOptions.emailInput = formatEmailInputs(values)
            }
            await releaseAllDfm(releaseQuoteOptions)
            onClose()
            message.success('Released')
        } catch (err) {
            const errorMsg = 'Failed to release DFM'

            message.error(`${errorMsg}, please try it again`)
            logger.exception(err, {
                extra: {
                    info: `AdminQuoteDetailsPage::onReleaseQuoteClick ${errorMsg}`,
                    quoteId,
                },
            })
        } finally {
            if (setIsReleasing) {
                setIsReleasing(false)
            }
        }
    })
}

export const onReleaseDfmOdpClick = releaseQuoteDfm => {
    const {
        quoteId,
        orderId,
        onClose,
        client,
        form,
        skipEmails,
        showCustamizableEmailSection,
    } = releaseQuoteDfm

    form.validateFields(async (error, values) => {
        try {
            const releaseDfmOdpOptions = {
                quoteId,
                orderId,
                client,
                skipDfmEmail: skipEmails,
                emailInput: null,
            }

            if (!skipEmails && showCustamizableEmailSection) {
                releaseDfmOdpOptions.emailInput = formatEmailInputs(values)
            }

            await releaseImDfmReport(releaseDfmOdpOptions)

            onClose()
            message.success('Released DFM successfully')
        } catch (err) {
            const errorMsg = 'Failed to release DFM'

            message.error(`${errorMsg}, please try it again`)
            logger.exception(err, {
                extra: {
                    info: `AdminOrderDetailsPage::onReleaseDfmOdpClick ${errorMsg}`,
                    quoteId,
                },
            })
        }
    })
}

export const getValidFunctionality = ({
    onClose,
    quoteId,
    orderId,
    isReleaseDfm,
    isReleaseDfmOdp,
    setIsReleasing,
    client,
    form,
    skipEmails,
    showCustamizableEmailSection,
}) => {
    const releaseQuoteDfm = {
        onClose,
        quoteId,
        orderId,
        isReleaseDfm,
        isReleaseDfmOdp,
        setIsReleasing,
        client,
        form,
        skipEmails,
        showCustamizableEmailSection,
    }

    if (isReleaseDfmOdp) {
        return onReleaseDfmOdpClick(releaseQuoteDfm)
    } else if (isReleaseDfm && !isReleaseDfmOdp) {
        return onReleaseDfmClick(releaseQuoteDfm)
    } else if (!isReleaseDfm) {
        return onReleaseQuoteClick(releaseQuoteDfm)
    }
}

export const getValidDescription = (isReleaseDfmOdp, isReleaseDfm) => {
    if (isReleaseDfmOdp) {
        return ODP_RELEASE_DFM_DESCRIPTION
    } else if (isReleaseDfm && !isReleaseDfmOdp) {
        return RELEASE_DFM_DESCRIPTION
    } else if (!isReleaseDfm) {
        return RELEASE_QUOTE_DESCRIPTION
    }
}

export const getAssociatedPartsWithSameMold = qouteItems => {
    const result = qouteItems?.reduce((value, acc) => {
        value[acc['selectedQuoteModelRevision']?.mold?.id] =
            value[acc['selectedQuoteModelRevision']?.mold?.id] || []
        value[acc['selectedQuoteModelRevision']?.mold?.id]?.push(acc)

        return value
    }, [])

    return result
}

export const sortMoldRecordTypes = (oneMold, anotherMold) =>
    !oneMold?.mold?.name ||
    oneMold?.mold?.name?.localeCompare(anotherMold?.mold?.name)
        ? 1
        : -1

export const getPartFileWithMiddleEllipses = (partFileName, charLen) => {
    const textRegexToBeRemoved = [
        /.step/i,
        /.pdf/i,
        /.stp/i,
        /.sdlprt/i,
        /.x_t/i,
    ]

    const newFileName = textRegexToBeRemoved.reduce(
        (currentFileName, regexToBeRemoved) =>
            currentFileName?.replace(regexToBeRemoved, ''),
        partFileName,
    )

    if (Math.floor(newFileName?.length / 2) > charLen) {
        return `${newFileName.substring(0, charLen)}...${newFileName.substring(
            newFileName.length - charLen,
            newFileName.length,
        )}`
    }
    return newFileName
}

export const getMoldNameWithStartEllipses = (moldName, charLen) => {
    if (Math.floor(moldName?.length / 2) > charLen) {
        return `${moldName.substring(0, 3)}...${moldName.substring(
            moldName.length - 9,
        )}`
    }
    return moldName
}

export const getCavityExpression = moldToolRevisionQmrs => {
    const cavityLength = moldToolRevisionQmrs?.length

    if (!cavityLength) {
        return '0x0'
    }

    if (cavityLength && cavityLength === 1) {
        return `1x${moldToolRevisionQmrs[0]?.cavityPerPart}`
    }
    if (cavityLength && cavityLength >= 2) {
        const cavities = moldToolRevisionQmrs.map(
            moldToolRevisionQmr => moldToolRevisionQmr?.cavityPerPart,
        )

        return cavities.join('+')
    }
}

export const getMoldToolRevisionIds = (moldToolRevisions, quoteId) => {
    let moldToolRevisionIds = []

    for (let revision in moldToolRevisions) {
        moldToolRevisions[revision].map(moldTool => {
            if (revision === quoteId) {
                return moldTool.moldToolRevisionIds
                    ? moldToolRevisionIds.push(moldTool.moldToolRevisionIds)
                    : ''
            }
        })
    }

    return moldToolRevisionIds
}

export const getMoldtems = moldItems =>
    moldItems
        ?.map(moldItem => {
            return { fileName: '', mold: moldItem }
        })
        ?.filter(Boolean)

export const isImQuoteBlocked = quote => {
    // Not an IM quote, no need to go further
    if (quote?.type !== PROCESSID_IM) {
        return false
    }

    return quote?.quoteModels?.items?.some(
        item =>
            typeof item?.selectedQuoteModelRevision?.modelConfiguration
                ?.productionLeadTime?.productionDays !== 'number',
    )
}

export const getCapitalizesDfmTags = tags => {
    return (
        tags.map(tag => {
            return Object.keys(DFM_TAGS_MAP).find(key => {
                if (DFM_TAGS_MAP[key] === tag) {
                    return DFM_TAGS_MAP[key]
                }
            })
        }) || []
    )
}
